import React, { useEffect, useRef, useState } from "react";
import { View, Text, StyleSheet, Pressable, Animated, Easing, Platform } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { useTheme } from "../../../context/ThemeContext";
import { useMobile } from "../../../hooks/useMobile";
import routes from "../../../navigation/routes";
import { Reveal, FONT_DISPLAY, FONT_MONO } from "./_shared";

const CHECKS = [
  "Generates a starter plan in 5–15 seconds.",
  "Review every extension and ring group before anything is created.",
  "Ask for changes in plain English (\"drop the IVR\", \"start at 100\").",
];

const EXTENSIONS = [
  { ext: "1001", name: "Front Desk" },
  { ext: "1002", name: "Dr. Patel" },
  { ext: "1003", name: "Dr. Reyes" },
  { ext: "1004", name: "Hygienist 1" },
];

export default function NtVoxcraftAiSection() {
  const { theme } = useTheme();
  const nav = useNavigation();
  const isMobile = useMobile();

  return (
    <View nativeID="assistant" style={[styles.section, { backgroundColor: theme.bg2, borderColor: theme.borderSoft, paddingVertical: isMobile ? 64 : 110 }]}>
      <View style={[styles.container, isMobile && styles.containerMobile]}>
        {/* LEFT — copy */}
        <Reveal style={[styles.left, isMobile && styles.leftMobile]}>
          <View style={[styles.tag, { backgroundColor: theme.pillBg, borderColor: theme.pillBorder }]}>
            <Text style={[styles.tagGlyph, { color: theme.brand2 }]}>◆</Text>
            <Text style={[styles.tagText, { color: theme.brand2 }]}>AI ASSISTANT · INCLUDED</Text>
          </View>
          <Text style={[styles.title, { color: theme.text }, isMobile && styles.titleMobile]}>Tell us about your business.</Text>
          <Text style={[styles.title, { color: theme.text }, isMobile && styles.titleMobile]}>We'll do the rest.</Text>
          <Text style={[styles.lede, { color: theme.textDim }]}>
            Answer five short questions, or just describe your business in your own words, and our setup AI
            proposes a complete starter plan: extensions for every person, ring groups for each team, an IVR
            menu, and a phone number. Review, edit, and ship.
          </Text>

          <View style={styles.checklist}>
            {CHECKS.map((c, i) => (
              <View key={i} style={[styles.checkRow, { borderBottomColor: theme.borderSoft, borderBottomWidth: i < CHECKS.length - 1 ? 1 : 0 }]}>
                <View style={[styles.checkBubble, { backgroundColor: "rgba(76,183,130,0.15)", borderColor: "rgba(76,183,130,0.3)" }]}>
                  <Text style={[styles.checkBubbleText, { color: theme.green }]}>✓</Text>
                </View>
                <Text style={[styles.checkText, { color: theme.textDim }]}>{c}</Text>
              </View>
            ))}
          </View>

          <Pressable onPress={() => nav.navigate(routes.onboarding)} style={({ pressed }) => [styles.btn, pressed && styles.btnPressed]}>
            <Text style={styles.btnText}>Try the wizard →</Text>
          </Pressable>
        </Reveal>

        {/* RIGHT — plan preview mockup */}
        <PlanPreview theme={theme} isMobile={isMobile} />
      </View>
    </View>
  );
}

// =============================================================================
// Plan-preview mockup — slight 3D tilt, cards fade in sequentially when the
// component scrolls into view. Mirrors v4's `.ai-mock` + `.ai-card` block.
// =============================================================================
function PlanPreview({ theme, isMobile }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (Platform.OS !== "web") { setInView(true); return; }
    const el = ref.current && (ref.current._node || ref.current);
    if (!el || typeof IntersectionObserver === "undefined") { setInView(true); return; }
    // hover-flatten effect comes from .voxcraft-ai-mock CSS in web/index.html
    if (el.classList) el.classList.add("voxcraft-ai-mock");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setInView(true); io.unobserve(e.target); } });
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Animated.View
      ref={ref}
      style={[
        styles.mock,
        { backgroundColor: theme.surface, borderColor: theme.borderSoft },
        isMobile && styles.mockMobile,
        // Subtle 3D tilt — flattens on hover via web-only style escape below.
        // We bake the static tilt into transform; the hover-flatten is a CSS
        // class added via web/index.html if you want it (kept off for now).
        Platform.OS === "web" && { transform: [{ perspective: 1200 }, { rotateY: "-3deg" }, { rotateX: "2deg" }] },
      ]}
    >
      <View style={styles.mockHead}>
        <View style={[styles.dot, { backgroundColor: "#f15a5a" }]} />
        <View style={[styles.dot, { backgroundColor: "#f1b250" }]} />
        <View style={[styles.dot, { backgroundColor: "#4cb782" }]} />
        <Text style={[styles.mockTitle, { color: theme.textMute }]}>AI · plan preview</Text>
      </View>

      {/* Terminal-style typing prompt — mirrors v4's `.ai-typing`. */}
      <TypingPrompt inView={inView} theme={theme} />

      <Card inView={inView} delay={0} theme={theme} title="SUMMARY">
        <Text style={[styles.summary, { color: theme.text }]}>
          "A 6-person dental practice with front desk, three clinicians, and admin. One reception ring group
          routes inbound calls. An IVR greets callers and offers billing or appointments."
        </Text>
      </Card>

      <Card inView={inView} delay={150} theme={theme} title="EXTENSIONS · 6">
        {EXTENSIONS.map((e) => (
          <View key={e.ext} style={styles.row}>
            <View style={[styles.badge, { backgroundColor: theme.pillBg, borderColor: theme.pillBorder }]}>
              <Text style={[styles.badgeText, { color: theme.brand2 }]}>{e.ext}</Text>
            </View>
            <Text style={[styles.rowText, { color: theme.text }]}>{e.name}</Text>
          </View>
        ))}
      </Card>

      <Card inView={inView} delay={300} theme={theme} title="RING GROUPS · 1">
        <View style={styles.row}>
          <View style={[styles.badge, { backgroundColor: "rgba(76,183,130,0.15)", borderColor: "rgba(76,183,130,0.35)" }]}>
            <Text style={[styles.badgeText, { color: theme.green }]}>2001</Text>
          </View>
          <Text style={[styles.rowText, { color: theme.text }]}>Reception → 1001, 1006</Text>
        </View>
      </Card>

      <Card inView={inView} delay={450} theme={theme} title="IVR · 1">
        <View style={styles.row}>
          <View style={[styles.badge, { backgroundColor: "rgba(241,178,80,0.15)", borderColor: "rgba(241,178,80,0.4)" }]}>
            <Text style={[styles.badgeText, { color: theme.orange }]}>5001</Text>
          </View>
          <Text style={[styles.rowText, { color: theme.text }]}>Main Greeting → 1 / 2 / 0</Text>
        </View>
      </Card>
    </Animated.View>
  );
}

// Typewriter terminal prompt — types out the command character by character,
// then a blinking caret stays. Resets each time the mock re-enters view.
const PROMPT = '$ ai propose-plan --business "dental practice, 6 people"';
function TypingPrompt({ inView, theme }) {
  const [text, setText] = useState("");
  const [caretOn, setCaretOn] = useState(true);

  useEffect(() => {
    if (!inView) return;
    setText("");
    let i = 0;
    const step = () => {
      i += 1;
      setText(PROMPT.slice(0, i));
      if (i < PROMPT.length) {
        timer = setTimeout(step, 36);
      }
    };
    let timer = setTimeout(step, 36);
    return () => clearTimeout(timer);
  }, [inView]);

  // Blinking caret
  useEffect(() => {
    const id = setInterval(() => setCaretOn((c) => !c), 450);
    return () => clearInterval(id);
  }, []);

  return (
    <View style={[
      styles.typing,
      { backgroundColor: "#07090b", borderColor: theme.borderSoft },
    ]}>
      <Text style={[styles.typingText, { color: theme.textDim }]}>
        {text}
        <Text style={{ color: theme.brand2, opacity: caretOn ? 1 : 0 }}>_</Text>
      </Text>
    </View>
  );
}

// Each card slides in 8px + fades, with a per-card delay.
function Card({ inView, delay, theme, title, children }) {
  const opacity = useRef(new Animated.Value(0)).current;
  const ty = useRef(new Animated.Value(8)).current;

  useEffect(() => {
    if (!inView) return;
    Animated.parallel([
      Animated.timing(opacity, { toValue: 1, duration: 500, delay, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
      Animated.timing(ty,      { toValue: 0, duration: 500, delay, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
    ]).start();
  }, [inView, delay, opacity, ty]);

  return (
    <Animated.View
      style={[
        styles.card,
        { backgroundColor: theme.surface2, borderColor: theme.borderSoft, opacity, transform: [{ translateY: ty }] },
      ]}>
      <Text style={[styles.cardTitle, { color: theme.textMute }]}>{title}</Text>
      {children}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  section: { paddingHorizontal: 28, borderTopWidth: 1, borderBottomWidth: 1 },
  container: { flexDirection: "row", alignItems: "center", maxWidth: 1280, marginHorizontal: "auto", width: "100%" },
  containerMobile: { flexDirection: "column" },

  left: { flex: 1, paddingRight: 56 },
  leftMobile: { width: "100%", paddingRight: 0, marginBottom: 40 },

  tag: {
    flexDirection: "row", alignItems: "center", alignSelf: "flex-start",
    borderWidth: 1, borderRadius: 999, paddingHorizontal: 11, paddingVertical: 6, marginBottom: 20,
  },
  tagGlyph: { fontSize: 10, marginRight: 8 },
  tagText: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 0.9, textTransform: "uppercase" },

  title: { fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: "800", letterSpacing: -1, lineHeight: 46 },
  titleMobile: { fontSize: 32, lineHeight: 36 },
  lede: { fontSize: 17, lineHeight: 27, marginTop: 16 },

  checklist: { marginTop: 24, marginBottom: 28 },
  checkRow: { flexDirection: "row", alignItems: "center", paddingVertical: 10 },
  checkBubble: {
    width: 22, height: 22, borderRadius: 11, borderWidth: 1,
    alignItems: "center", justifyContent: "center", marginRight: 12, flexShrink: 0,
  },
  checkBubbleText: { fontSize: 12, fontWeight: "800" },
  checkText: { fontSize: 14.5, lineHeight: 22, flex: 1 },

  btn: { alignSelf: "flex-start", paddingHorizontal: 22, paddingVertical: 13, borderRadius: 10, backgroundColor: "#2570BD" },
  btnText: { color: "#fff", fontSize: 14, fontWeight: "600" },
  btnPressed: { opacity: 0.85, transform: [{ translateY: -1 }] },

  // Mockup
  mock: {
    flex: 1.05,
    padding: 22, borderRadius: 16, borderWidth: 1,
    // Big drop shadow for the floating-screen vibe (web only via shadow*).
    shadowColor: "#000", shadowOpacity: 0.45, shadowRadius: 80, shadowOffset: { width: 0, height: 40 },
  },
  mockMobile: { width: "100%", flex: undefined },
  mockHead: { flexDirection: "row", alignItems: "center", marginBottom: 16 },
  dot: { width: 10, height: 10, borderRadius: 5, marginRight: 6 },
  mockTitle: { flex: 1, textAlign: "center", fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 0.4 },

  typing: {
    paddingVertical: 10, paddingHorizontal: 12,
    borderRadius: 6, borderWidth: 1, marginBottom: 14, overflow: "hidden",
  },
  typingText: {
    fontFamily: FONT_MONO, fontSize: 12, lineHeight: 18,
    whiteSpace: "nowrap",
  },

  card: { padding: 14, paddingHorizontal: 16, borderRadius: 6, borderWidth: 1, marginBottom: 10 },
  cardTitle: { fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 0.5, fontWeight: "700", marginBottom: 10, textTransform: "uppercase" },

  summary: { fontSize: 13, lineHeight: 20 },

  row: { flexDirection: "row", alignItems: "center", paddingVertical: 5 },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4, borderWidth: 1, marginRight: 10 },
  badgeText: { fontFamily: FONT_MONO, fontSize: 11, fontWeight: "700", letterSpacing: 0.2 },
  rowText: { fontSize: 13 },
});
